/** @jsxImportSource @emotion/react */
import { useFieldArray, useFormContext } from "react-hook-form";
import { ConfirmActionModal } from "../../components/ConfirmActionModal";
import { ButtonAction } from "../../components/NewButtons";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { TimeslotDays } from "./model/TimeslotTemplatesSuccessResponse";
import { css } from "@emotion/react";
import { CheckboxField } from "../../components/forms/fields/CheckboxField";
import { StyledText } from "../../assets/typography/typography";
import { ErrorMessage } from "@hookform/error-message";
import { colors } from "../../components/colors";
import { FormRow } from "../common/hook-form";
import { TimePickerInput } from "../../components/forms/inputs/TimePickerInput";
import { capitalize } from "lodash";
import { minutesToSeconds } from "../../util";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { Divider } from "../../components/Divider";
import { CreateTimeslotsFormValues } from "./CreateTimeslotsSelector";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  showCustomDuration: boolean;
  setShowCustomDuration: (showCustomDuration: boolean) => void;
};

const daysOfWeekWrapperStyle = css`
  display: block;
`;
const dayOfWeekStyle = css`
  display: inline-block;
  margin-bottom: 4px;
  margin-top: 4px;
  margin-right: 16px;
`;

export const CreateTimeslotsModal = ({
  isOpen,
  onClose,
  onSubmit,
  showCustomDuration,
  setShowCustomDuration,
}: Props) => {
  const methods = useFormContext<CreateTimeslotsFormValues>();
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "days",
  });

  return (
    <ConfirmActionModal
      title="Create timeslots"
      subtitle="This will create timeslots between start and end time with the period you select - e.g. 10:00 and 20:00 with 5 minutes, will result in timeslots 10:05, 10:10, .. 19:55, 20:00 on the days you select."
      isOpen={isOpen}
      width={600}
      onClose={onClose}
      confirmButton={
        <ButtonAction type="button" onClick={onSubmit}>
          Create
        </ButtonAction>
      }
    >
      <div css={daysOfWeekWrapperStyle}>
        {Object.keys(TimeslotDays)
          .filter((day) => isNaN(Number(day)))
          .map((day, i) => (
            <div key={i} css={dayOfWeekStyle}>
              <CheckboxField
                label={capitalize(day)}
                checked={fields.some(
                  (field) =>
                    field.value ===
                    TimeslotDays[day as keyof typeof TimeslotDays]
                )}
                onChange={(e) => {
                  if (e.target.checked) {
                    append({
                      value: TimeslotDays[day as keyof typeof TimeslotDays],
                    });
                  } else {
                    const index = fields.findIndex(
                      (field) =>
                        field.value ===
                        TimeslotDays[day as keyof typeof TimeslotDays]
                    );
                    remove(index);
                  }
                }}
              />
            </div>
          ))}
      </div>
      <ErrorMessage
        errors={methods.formState.errors}
        name="days"
        render={({ message }) => (
          <StyledText color={colors.errorDark} className="bodyTextRegular mb-0">
            {message}
          </StyledText>
        )}
      />
      <FormRow>
        <TimePickerInput
          name="startTime"
          label="Start time"
          placeholder="Start time"
        />
        <TimePickerInput
          name="endTime"
          label="End time"
          placeholder="End time"
        />
      </FormRow>
      <FormRow>
        <SelectInput
          name="duration"
          label="Timeslot interval"
          isDisabled={showCustomDuration}
          helperText="How many minutes between each timeslot?"
          placeholder="Choose number of minutes between each timeslot"
          options={[
            {
              label: "1 min",
              value: minutesToSeconds(1),
            },
            {
              label: "2 min",
              value: minutesToSeconds(2),
            },
            {
              label: "5 min",
              value: minutesToSeconds(5),
            },
            {
              label: "10 min",
              value: minutesToSeconds(10),
            },
            {
              label: "12 min",
              value: minutesToSeconds(12),
            },
            {
              label: "15 min",
              value: minutesToSeconds(15),
            },
            {
              label: "20 min",
              value: minutesToSeconds(20),
            },
          ]}
        />
      </FormRow>
      <CheckboxField
        label="Custom interval"
        checked={showCustomDuration}
        onChange={(e) => {
          setShowCustomDuration(!showCustomDuration);
          if (e.target.checked) {
            methods.setValue("duration", 0);
          } else {
            methods.setValue("customDuration", 0);
          }
        }}
      />
      {showCustomDuration && (
        <FormRow>
          <TextInput
            name="customDuration"
            label="Custom timeslot interval"
            helperText="Enter amount of minutes between each timeslot"
            placeholder="Enter amount of minutes between each timeslot"
            type="number"
          />
        </FormRow>
      )}
      <Divider />
      <StyledText className="bodyTextSemibold mb-0" color={colors.textPrimary}>
        Timeslot configuration
      </StyledText>
      <FormRow>
        <TextInput
          name="maxOrders"
          label="Max orders"
          helperText="Enter amount of orders per timeslot"
          placeholder="Max orders per timeslot"
          type="number"
        />
        <TextInput
          name="maxPickupOrders"
          label="Max pickup orders"
          helperText="Enter amount of pickup orders per timeslot"
          placeholder="Max pickup orders per timeslot"
          type="number"
        />
      </FormRow>
      <FormRow>
        <TextInput
          name="maxDeliveryOrders"
          label="Max delivery orders"
          helperText="Enter amount of delivery orders per timeslot"
          placeholder="Max delivery orders per timeslot"
          type="number"
        />
        <TextInput
          name="maxUnits"
          label="Max units"
          helperText="Enter amount of units per timeslot"
          placeholder="Max units per timeslot"
          type="number"
        />
      </FormRow>
      <FormRow>
        <TextInput
          name="preparationTime"
          label="Preparation time"
          helperText="Enter amount of seconds to prepare an order"
          placeholder="Preparation time for an order"
          type="number"
        />
        <TextInput
          name="deliveryTime"
          label="Delivery time"
          helperText="Enter amount of seconds to deliver an order"
          placeholder="Delivery time for an order"
          type="number"
        />
      </FormRow>
    </ConfirmActionModal>
  );
};
